import React from 'react';
import Icon from '@/components/ui/AppIcon';
import RisingParticlesScene from './RisingParticlesScene';

import Link from 'next/link';

interface CTASectionProps {
    onBookConsultation: () => void;
}

const CTASection = ({ onBookConsultation }: CTASectionProps) => {
    return (
        <section className="py-32 bg-slate-50 relative overflow-hidden">
            {/* Background Particles */}
            <RisingParticlesScene />

            {/* Background Texture - Minimalist Grid */}
            <div className="absolute inset-0 z-0 opacity-[0.03]" style={{
                backgroundImage: 'linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)',
                backgroundSize: '40px 40px'
            }}></div>

            <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12">
                <div className="grid lg:grid-cols-12 gap-16 lg:gap-8 items-center">

                    {/* Left: Content */}
                    <div className="lg:col-span-7">
                        <div className="inline-flex items-center space-x-2 border border-slate-200 bg-white px-4 py-2 rounded-full mb-8">
                            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                            <span className="text-xs font-bold uppercase tracking-widest text-slate-900">Limited Time Offer</span>
                        </div>

                        <h2 className="text-5xl lg:text-7xl font-bold text-slate-900 tracking-tighter leading-[1.05] mb-8">
                            Ready to Dominate <br />
                            <span className="text-indigo-600">Your Market?</span>
                        </h2>

                        <p className="text-xl lg:text-2xl text-slate-600 font-light mb-12 max-w-2xl leading-relaxed">
                            Get a free comprehensive brand audit worth <span className="font-semibold text-slate-900">₹5,000</span> and discover exactly how to outrank your competitors and capture more customers.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4">
                            <button
                                onClick={onBookConsultation}
                                className="inline-flex items-center justify-center px-10 py-5 text-sm font-bold uppercase tracking-widest text-white bg-slate-900 hover:bg-indigo-600 transition-all duration-300 shadow-xl shadow-slate-900/10"
                            >
                                Claim Your Free Audit
                                <Icon name="ArrowRightIcon" size={20} className="ml-2" />
                            </button>

                            <Link
                                href="/services?category=growth#pricing-plans"
                                className="inline-flex items-center justify-center px-10 py-5 text-sm font-bold uppercase tracking-widest text-slate-900 bg-white border-2 border-slate-900 hover:bg-slate-50 transition-all duration-300"
                            >
                                View Pricing Plans
                            </Link>
                        </div>
                    </div>

                    {/* Right: Guarantee Grid */}
                    <div className="lg:col-span-5 bg-white p-12 border border-slate-200 shadow-2xl relative">
                        {/* Decorative Corner */}
                        <div className="absolute top-0 right-0 w-24 h-24 bg-slate-50 border-b border-l border-slate-200"></div>

                        <div className="space-y-12">
                            <div className="flex items-start gap-6">
                                <div className="w-12 h-12 bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 flex-shrink-0">
                                    <Icon name="ClockIcon" size={24} variant="outline" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-slate-900 mb-1">24-Hour Response</h3>
                                    <p className="text-slate-500 text-sm font-medium">Quick turnaround on all inquiries</p>
                                </div>
                            </div>

                            <div className="flex items-start gap-6">
                                <div className="w-12 h-12 bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 flex-shrink-0">
                                    <Icon name="ShieldCheckIcon" size={24} variant="outline" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-slate-900 mb-1">No Long-Term Contracts</h3>
                                    <p className="text-slate-500 text-sm font-medium">Flexible engagement options</p>
                                </div>
                            </div>

                            <div className="flex items-start gap-6">
                                <div className="w-12 h-12 bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 flex-shrink-0">
                                    <Icon name="ChartBarIcon" size={24} variant="outline" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-slate-900 mb-1">Results Guaranteed</h3>
                                    <p className="text-slate-500 text-sm font-medium">Or your money back</p>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
};

export default CTASection;